import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import {
  EOBExtractionResponseDTO,
} from 'dtos';
import {
  useNotify,
} from 'core/hooks';
import {
  useStartEOBExtraction,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/hooks';
import {
  EOBDialogParams,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog';
import {
  paymentsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  getEmptySelection,
  ParseEOBSource,
  PendingEOBSelection,
} from 'pages/Dashboard/pages/Billing/Payments/ParseEOBDialog/utils';
import {
  FileLocalUrl,
} from 'pages/Dashboard/components/FilePicker';
import ParseEOBDialog from 'pages/Dashboard/pages/Billing/Payments/ParseEOBDialog';
import MinimizedEOBScan, {
  EOBScanStatus,
} from 'pages/Dashboard/pages/Billing/Payments/ParseEOBDialog/MinimizedEOBScan';
import useElapsedSeconds from 'core/hooks/useElapsedSeconds';

type EOBScanMode = 'closed' | 'dialog' | 'minimized';

function buildEOBDialogParams(
  extracted: EOBExtractionResponseDTO,
  finalPayload: File,
  eobExtractionJobId?: number,
): EOBDialogParams {
  const eobData = extracted?.eobWithInsurancePayment;
  return {
    parsedEOBData: { ...eobData, eob: { ...eobData?.eob, eobId: undefined } },
    eobNumber: eobData?.eob?.eobNumber ?? undefined,
    parsedEOBFile: finalPayload,
    parsedEOBErrors: omit(extracted, 'eobWithInsurancePayment'),
    eobExtractionJobId,
  };
}

export default function EOBScanContainer() {
  const notify = useNotify();
  const [mode, setMode] = useState<EOBScanMode>('closed');
  const [scanStatus, setScanStatus] = useState<EOBScanStatus>('idle');
  const [pendingEOBDialogParams, setPendingEOBDialogParams] = useState<
    EOBDialogParams | null
  >(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [source, setSource] = useState<ParseEOBSource | null>(null);
  const [pendingSelection, setPendingSelection] = useState<PendingEOBSelection>(
    getEmptySelection(null),
  );
  const [isNormalizing, setIsNormalizing] = useState(false);
  const [finalPayload, setFinalPayload] = useState<File | null>(null);
  const [generatedPdfCache, setGeneratedPdfCache] = useState<FileLocalUrl | null>(null);

  const modeRef = useRef(mode);
  const finalPayloadRef = useRef(finalPayload);
  const scanStatusRef = useRef(scanStatus);
  const pendingEOBDialogParamsRef = useRef(pendingEOBDialogParams);

  modeRef.current = mode;
  finalPayloadRef.current = finalPayload;
  scanStatusRef.current = scanStatus;
  pendingEOBDialogParamsRef.current = pendingEOBDialogParams;

  const resetScanUi = useCallback(() => {
    setMode('closed');
    setScanStatus('idle');
    setPendingEOBDialogParams(null);
    setErrorMessage(null);
    setSource(null);
    setPendingSelection(getEmptySelection(null));
    setIsNormalizing(false);
    setFinalPayload(null);
    setGeneratedPdfCache(null);
  }, []);

  const onExtracted = useCallback((
    extracted: EOBExtractionResponseDTO,
    eobExtractionJobId?: number,
  ) => {
    const payload = finalPayloadRef.current;
    if (!isNil(payload)) {
      const params = buildEOBDialogParams(extracted, payload, eobExtractionJobId);

      if (modeRef.current === 'minimized') {
        setPendingEOBDialogParams(params);
        setScanStatus('completed');
        notify({
          message: 'EOB scan complete — click to review',
          variant: 'success',
        });
      } else {
        paymentsEventChannel.emit('onEOBDialogOpen', params);
        resetScanUi();
      }
    }
  }, [resetScanUi, notify]);

  const onExtractionFailed = useCallback((failedMessage: string) => {
    if (modeRef.current === 'minimized') {
      setScanStatus('failed');
      setErrorMessage(failedMessage);
    }
  }, []);

  const {
    startEOBExtraction,
    cancelExtraction,
    isProcessing: isExtracting,
  } = useStartEOBExtraction({
    onExtracted,
    onExtractionFailed,
  });

  const closeScan = useCallback(() => {
    cancelExtraction();
    resetScanUi();
  }, [cancelExtraction, resetScanUi]);

  const isScanProcessing = isExtracting || isNormalizing;
  const scanElapsedSeconds = useElapsedSeconds(isScanProcessing);

  useEffect(() => {
    if (isScanProcessing) {
      setScanStatus('processing');
    }
  }, [isScanProcessing]);

  const openCompletedEOBDialog = useCallback(() => {
    const params = pendingEOBDialogParamsRef.current;
    let opened = false;
    if (scanStatusRef.current === 'completed' && !isNil(params)) {
      paymentsEventChannel.emit('onEOBDialogOpen', params);
      closeScan();
      opened = true;
    }
    return opened;
  }, [closeScan]);

  const handleParseEOBScanOpen = useCallback(() => {
    const currentMode = modeRef.current;

    if (currentMode === 'closed') {
      setMode('dialog');
    } else if (currentMode === 'minimized') {
      const opened = openCompletedEOBDialog();
      if (!opened) {
        setMode('dialog');
      }
    }
  }, [openCompletedEOBDialog]);

  useEffect(() => {
    const unsubscribe = paymentsEventChannel.on('onParseEOBScanOpen', handleParseEOBScanOpen);

    return unsubscribe;
  }, [handleParseEOBScanOpen]);

  const handleMinimize = useCallback(() => {
    setMode('minimized');
  }, []);

  const handleMinimizedClick = useCallback(() => {
    const opened = openCompletedEOBDialog();
    if (!opened) {
      setMode('dialog');
    }
  }, [openCompletedEOBDialog]);

  return mode !== 'closed' ? (
    <>
      {mode === 'dialog' && (
        <ParseEOBDialog
          source={source}
          setSource={setSource}
          pendingSelection={pendingSelection}
          setPendingSelection={setPendingSelection}
          isNormalizing={isNormalizing}
          setIsNormalizing={setIsNormalizing}
          setFinalPayload={setFinalPayload}
          generatedPdfCache={generatedPdfCache}
          setGeneratedPdfCache={setGeneratedPdfCache}
          isExtracting={isExtracting}
          scanElapsedSeconds={scanElapsedSeconds}
          startEOBExtraction={startEOBExtraction}
          onCancel={closeScan}
          onMinimize={handleMinimize}
        />
      )}
      {mode === 'minimized' && (
        <MinimizedEOBScan
          scanStatus={scanStatus}
          errorMessage={errorMessage}
          onClick={handleMinimizedClick}
        />
      )}
    </>
  ) : null;
}
